import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Table, Select, Row, Col, Button, Space, message } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import EditableDrawer from '../../../../components/EditableDrawer';
import CentralParcelDetailDrawer from '../../../central/parcels/components/CentralParcelDetailDrawer';
import AsyncReportDownloadModal, {
    dashboardAsyncReportLabelI18nKeys,
    useAsyncReportExportCooldown,
} from '../../../../components/asyncReportDownload';
import { getUserInfo } from '../../../../utils/auth';
import { showError } from '../../../../utils/exceptionUtils';
import { QUERY_DAILY_HUB_OPERATION_DETAIL_LIST } from '../../../../graphql/queries/dashboard/queryDailyHubOperationDetailList';
import { QUERY_SORTING_HOURLY_DETAIL_LIST } from '../../../../graphql/queries/dashboard/querySortingHourlyDetailList';
import {
    GENERATE_SORTING_DETAIL_DOWNLOAD_REPORT,
    GenerateSortingDetailDownloadReportResponse,
    GenerateSortingDetailDownloadReportVariables,
} from '../../../../graphql/mutations/dashboard/generateSortingDetailDownloadReport';
import {
    GENERATE_SORTING_HOURLY_DETAIL_DOWNLOAD_REPORT,
    GenerateSortingHourlyDetailDownloadReportResponse,
    GenerateSortingHourlyDetailDownloadReportVariables,
} from '../../../../graphql/mutations/dashboard/generateSortingHourlyDetailDownloadReport';
import {
    QueryDailyHubOperationDetailListResponse,
    DailyHubOperationDetailType,
    DailyHubOperationDrillTypeInput,
} from '../../../../graphql/types/dashboard/DailyHubOperationDetailTypes';
import { QUERY_SORTING_CROSS_BORDER_ONLINE_24H_DETAIL_LIST } from '../../../../graphql/queries/dashboard/querySortingCrossBorderOnline24hDetailList';
import {
    GENERATE_SORTING_CROSS_BORDER_ONLINE_24H_DETAIL_DOWNLOAD_REPORT,
    GenerateSortingCrossBorderOnline24hDetailDownloadReportResponse,
    GenerateSortingCrossBorderOnline24hDetailDownloadReportVariables,
} from '../../../../graphql/mutations/dashboard/generateSortingCrossBorderOnline24hDetailDownloadReport';
import {
    QuerySortingCrossBorderOnline24hDetailListResponse,
    SortingCrossBorderOnline24hDetailRow,
} from '../../../../graphql/types/dashboard/SortingCrossBorderOnline24hDetailTypes';
import { buildCrossBorderOnline24hDetailColumns } from './crossBorderOnline24hDetailColumns';
import { formatUtcToPacific } from '../../../../utils/timezoneFormatter';
import { stripOrderEventDataPrefix } from '../../../../constants/externalEventCodes';
import type { SortingDashboardVariant } from '../sortingDashboardVariantConfig';
import { WarehouseBusinessType } from '../../../../graphql/types/dashboard/SortingDashboardTypes';

function parseUtcToUnix(utcStr: string | null): number | null {
    if (!utcStr) return null;
    const d = new Date(utcStr);
    return isNaN(d.getTime()) ? null : Math.floor(d.getTime() / 1000);
}

/** 入库和出库时间间隔（小时），保留2位小数：(departure_unix - arrival_unix) / 3600 */
function formatNoUpdateWrongSortEverStatus(v: number | null | undefined): string {
    if (v === 1) return '错分过一段码';
    if (v === 2) return '错分过二段码';
    return '否';
}

function computeArrivalDepartureHours(arrivalTime: string | null, departureTime: string | null): string {
    const a = parseUtcToUnix(arrivalTime);
    const b = parseUtcToUnix(departureTime);
    if (a == null || b == null) return '-';
    const hours = (b - a) / 3600;
    return hours.toFixed(2);
}

export type SortingDashboardDrillType =
    | 'nextDayHandoverRate'
    | 'fbtSameDayHandoverRate'
    | 'cbtNextDayHandoverRate'
    | 'shouldArriveNotArriveRate'
    | 'shouldArriveNotArriveCount'
    | 'notCorrectlyPackedCount'
    | 'bagLabelNotShippedCount'
    | 'noDepartureActionCount'
    | 'outbound12hRate'
    | 'ascanMissRate'
    | 'transferArrivalMissRate'
    | 'overallMissScanRate'
    | 'outboundMissRate'
    | 'wrongSortRate'
    | 'wrongSortToWrongLineABCount'
    | 'wrongSortToWrongHubACount'
    | 'wrongSortToWrongWhsBCount'
    | 'wrongSortToWrongLineCCount'
    | 'crossBorderOnline24hRate';

const DRILL_TO_INPUT: Partial<Record<SortingDashboardDrillType, DailyHubOperationDrillTypeInput>> = {
    nextDayHandoverRate: DailyHubOperationDrillTypeInput.NEXT_DAY_HANDOVER,
    // FBT/CBT 拆行走同一个后端 drill 枚举，按 customerName 参数区分 FBT / CBT 明细
    fbtSameDayHandoverRate: DailyHubOperationDrillTypeInput.NEXT_DAY_HANDOVER,
    cbtNextDayHandoverRate: DailyHubOperationDrillTypeInput.NEXT_DAY_HANDOVER,
    shouldArriveNotArriveRate: DailyHubOperationDrillTypeInput.SHOULD_ARRIVE_NOT_ARRIVE,
    shouldArriveNotArriveCount: DailyHubOperationDrillTypeInput.SHOULD_ARRIVE_NOT_ARRIVE,
    notCorrectlyPackedCount: DailyHubOperationDrillTypeInput.SHOULD_ARRIVE_NOT_CORRECTLY_PACKED,
    bagLabelNotShippedCount: DailyHubOperationDrillTypeInput.SHOULD_ARRIVE_BAG_LABEL_NOT_SHIPPED,
    noDepartureActionCount: DailyHubOperationDrillTypeInput.SHOULD_ARRIVE_NO_DEPARTURE_ACTION,
    outbound12hRate: DailyHubOperationDrillTypeInput.OUTBOUND_EXCEED_12H,
    ascanMissRate: DailyHubOperationDrillTypeInput.NO_ASCAN_DELIVERY,
    transferArrivalMissRate: DailyHubOperationDrillTypeInput.TRANSFER_ARRIVAL_MISSED_SCAN,
    overallMissScanRate: DailyHubOperationDrillTypeInput.OVERALL_MISS_SCAN,
    outboundMissRate: DailyHubOperationDrillTypeInput.OUTBOUND_MISSED_SCAN,
    wrongSortRate: DailyHubOperationDrillTypeInput.WRONG_SORT,
    wrongSortToWrongLineABCount: DailyHubOperationDrillTypeInput.WRONG_SORT_TO_WRONG_LINE_AB,
    wrongSortToWrongHubACount: DailyHubOperationDrillTypeInput.WRONG_SORT_TO_WRONG_HUB_A,
    wrongSortToWrongWhsBCount: DailyHubOperationDrillTypeInput.WRONG_SORT_TO_WRONG_WHS_B,
    wrongSortToWrongLineCCount: DailyHubOperationDrillTypeInput.WRONG_SORT_TO_WRONG_LINE_C,
};

export interface HubOptionForDetail {
    id: number;
    name: string;
}

interface SortingDashboardDetailDrawerProps {
    visible: boolean;
    onClose: () => void;
    title: string;
    drillType: SortingDashboardDrillType;
    statDateStart: string;
    statDateEnd: string;
    hubId: number;
    /** 与一级页一致：`sorting`（分拣）/ `quality`（断更），用于 i18n */
    dashboardVariant?: SortingDashboardVariant;
    /** 主页返回的 HUB 列表，用于组成详情页 HUB 下拉；不传则从当前页明细中提取 */
    hubList?: HubOptionForDetail[];
    /** 与主表 querySortingDashboard 一致；1=分拣仓，2=尾程仓 */
    businessType?: number;
    customerName?: string | null;
    /** 小时窗起始（与主查询一致；0~23 默认全天，非默认时走 V2 hourly drill 链路） */
    windowStartHour?: number;
    /** 小时窗结束（与主查询一致；0~23 默认全天，非默认时走 V2 hourly drill 链路） */
    windowEndHour?: number;
    /** 当前 hub 的 sortingCode，用于控制列可见性；全美或未知时为 null */
    hubSortingCode?: string | null;
}

const SortingDashboardDetailDrawer: React.FC<SortingDashboardDetailDrawerProps> = ({
    visible,
    onClose,
    title,
    drillType,
    statDateStart,
    statDateEnd,
    hubId,
    hubList,
    dashboardVariant = 'sorting',
    businessType = WarehouseBusinessType.LAST_MILE,
    customerName = null,
    windowStartHour = 0,
    windowEndHour = 23,
    hubSortingCode = null,
}) => {
    // 跨境24h上网率下钻走独立 query + mutation（Hologres 单一路径），不与既有 daily/hourly drill 复用输入
    const isCrossBorderOnline24h = drillType === 'crossBorderOnline24hRate';
    // 仅当主查询走小时窗（非全天 0~23）时切到 V2 hourly drill 链路，保证明细范围与指标口径一致
    const useHourlyDrill = !isCrossBorderOnline24h && (windowStartHour !== 0 || windowEndHour !== 23);
    const { t, i18n } = useTranslation();
    const dt = useCallback(
        (suffix: string) => t(`dashboard.${dashboardVariant}.detailDrawer.${suffix}`),
        [t, dashboardVariant]
    );
    const { exportLoading, runExportWithCooldown, getExportButtonLabel } = useAsyncReportExportCooldown();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selectedHubIds, setSelectedHubIds] = useState<number[]>([]);
    const [parcelDetailVisible, setParcelDetailVisible] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState<string>('');
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [downloadCompleteModalVisible, setDownloadCompleteModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');
    // Long 经 JSON 可能为字符串 "0"：须与数字统一，否则 isAllUs 为 false 时不展示 HUB 列/筛选，但请求仍按 hubId=0
    const safeHubId = (() => {
        const n = Number(hubId);
        return Number.isFinite(n) ? n : 0;
    })();
    const isAllUs = safeHubId === 0;
    const safeSelectedHubIds = useMemo(() => {
        return (selectedHubIds ?? [])
            .map((v) => Number(v))
            .filter((v) => Number.isFinite(v));
    }, [selectedHubIds]);

    const [generateReportMutation] = useMutation<
        GenerateSortingDetailDownloadReportResponse,
        GenerateSortingDetailDownloadReportVariables
    >(GENERATE_SORTING_DETAIL_DOWNLOAD_REPORT);
    const [generateHourlyReportMutation] = useMutation<
        GenerateSortingHourlyDetailDownloadReportResponse,
        GenerateSortingHourlyDetailDownloadReportVariables
    >(GENERATE_SORTING_HOURLY_DETAIL_DOWNLOAD_REPORT);
    const [generateCrossBorderReportMutation] = useMutation<
        GenerateSortingCrossBorderOnline24hDetailDownloadReportResponse,
        GenerateSortingCrossBorderOnline24hDetailDownloadReportVariables
    >(GENERATE_SORTING_CROSS_BORDER_ONLINE_24H_DETAIL_DOWNLOAD_REPORT);

    useEffect(() => {
        if (!visible) setSelectedRowKeys([]);
    }, [visible]);

    const input = useMemo(
        () => {
            if (isCrossBorderOnline24h) {
                return {
                    statDateStart,
                    statDateEnd,
                    hubId: safeHubId,
                    hubIds: safeSelectedHubIds.length > 0 ? safeSelectedHubIds : null,
                    customerName: customerName ?? null,
                    page,
                    size: pageSize,
                    sortBy: 'statDate',
                    sortDirection: 'DESC',
                };
            }
            const base = {
                statDateStart,
                statDateEnd,
                hubId: safeHubId,
                hubIds: safeSelectedHubIds.length > 0 ? safeSelectedHubIds : null,
                drillType: DRILL_TO_INPUT[drillType],
                page,
                size: pageSize,
                // 不能按 id 排序：id 是主键，MySQL 会为省 filesort 改用主键倒序全表扫，
                // 命中行少于 size 时无法提前终止，600w 行扫满 15s 触发 max_execution_time
                sortBy: 'statDate',
                sortDirection: 'DESC',
                customerName: customerName ?? null,
                businessType,
            };
            return useHourlyDrill
                ? { ...base, windowStartHour, windowEndHour }
                : base;
        },
        [isCrossBorderOnline24h, statDateStart, statDateEnd, safeHubId, safeSelectedHubIds, drillType, page, pageSize, customerName, businessType, useHourlyDrill, windowStartHour, windowEndHour]
    );

    const { data, loading } = useQuery<QueryDailyHubOperationDetailListResponse>(
        useHourlyDrill ? QUERY_SORTING_HOURLY_DETAIL_LIST : QUERY_DAILY_HUB_OPERATION_DETAIL_LIST,
        {
            variables: { input },
            skip: !visible || isCrossBorderOnline24h,
        }
    );

    const { data: crossBorderData, loading: crossBorderLoading } = useQuery<QuerySortingCrossBorderOnline24hDetailListResponse>(
        QUERY_SORTING_CROSS_BORDER_ONLINE_24H_DETAIL_LIST,
        {
            variables: { input },
            skip: !visible || !isCrossBorderOnline24h,
        }
    );

    const response = data?.queryDailyHubOperationDetailList;
    const crossBorderResponse = crossBorderData?.querySortingCrossBorderOnline24hDetailList;
    const content: DailyHubOperationDetailType[] = isCrossBorderOnline24h ? [] : (response?.content ?? []);
    const crossBorderContent: SortingCrossBorderOnline24hDetailRow[] = isCrossBorderOnline24h ? (crossBorderResponse?.content ?? []) : [];
    const totalElements = isCrossBorderOnline24h ? (crossBorderResponse?.totalElements ?? 0) : (response?.totalElements ?? 0);
    const tableLoading = isCrossBorderOnline24h ? crossBorderLoading : loading;

    const hubOptions = useMemo(() => {
        if (hubList && hubList.length > 0) {
            return hubList.map((hub) => ({ label: hub.name, value: hub.id }));
        }
        const seen = new Map<number, string>();
        const rowsForHubs = isCrossBorderOnline24h ? crossBorderContent : content;
        rowsForHubs.forEach((row) => {
            if (row.hubId != null && row.hubName?.trim()) seen.set(row.hubId, row.hubName.trim());
        });
        return Array.from(seen.entries())
            .sort((a, b) => a[1].localeCompare(b[1]))
            .map(([id, name]) => ({ label: name, value: id }));
    }, [hubList, content, crossBorderContent, isCrossBorderOnline24h]);

    const crossBorderColumns = useMemo(
        () =>
            buildCrossBorderOnline24hDetailColumns({
                onOpenParcel: (oid: string) => {
                    setSelectedOrderId(oid);
                    setParcelDetailVisible(true);
                },
            }),
        []
    );

    const baseColumns = useMemo(() => {
        const cols: {
            title: string;
            dataIndex: string;
            key: string;
            render?: (v: unknown, r: DailyHubOperationDetailType) => React.ReactNode;
        }[] = [
            {
                title: dt('trackingNumber'),
                dataIndex: 'trackingNumber',
                key: 'trackingNumber',
                render: (text: unknown, record: DailyHubOperationDetailType) => {
                    const tn = (text as string) ?? record.trackingNumber ?? '-';
                    const oid = record.orderId;
                    if (oid == null || oid === 0) return <span>{tn}</span>;
                    return (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                setSelectedOrderId(String(oid));
                                setParcelDetailVisible(true);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {tn}
                        </a>
                    );
                },
            },
        ];
        // 客户名称列：直接读明细表 ETL 写入的 customer_name，信任数据源
        cols.push({
            title: dt('clientName'),
            dataIndex: 'customerName',
            key: 'customerName',
            render: (text: unknown) => {
                const name = (text as string) ?? '';
                return <span>{name || '-'}</span>;
            },
        });
        if (isAllUs) {
            cols.push({ title: dt('hubName'), dataIndex: 'hubName', key: 'hubName' });
        }
        if (drillType === 'shouldArriveNotArriveRate' || drillType === 'shouldArriveNotArriveCount' || drillType === 'notCorrectlyPackedCount' || drillType === 'bagLabelNotShippedCount' || drillType === 'noDepartureActionCount' || drillType === 'outbound12hRate' || drillType === 'outboundMissRate') {
            cols.push({
                title: dt('arrivalTime'),
                dataIndex: 'arrivalTime',
                key: 'arrivalTime',
                render: (v: unknown) => formatUtcToPacific(v as string | null),
            });
        }
        if (drillType === 'outbound12hRate') {
            cols.push({
                title: dt('departureTime'),
                dataIndex: 'departureTime',
                key: 'departureTime',
                render: (v: unknown) => formatUtcToPacific(v as string | null),
            });
            cols.push({
                title: dt('arrivalDepartureHours'),
                dataIndex: '_arrivalDepartureHours',
                key: '_arrivalDepartureHours',
                render: (_: unknown, r: DailyHubOperationDetailType) =>
                    computeArrivalDepartureHours(r.arrivalTime, r.departureTime),
            });
        }
        if (drillType === 'ascanMissRate' || drillType === 'transferArrivalMissRate' || drillType === 'overallMissScanRate') {
            cols.push({
                title: dt('deliveryTime'),
                dataIndex: 'deliveryTime',
                key: 'deliveryTime',
                render: (v: unknown) => formatUtcToPacific(v as string | null),
            });
        }
        const wrongSortDrills: SortingDashboardDrillType[] = [
            'wrongSortRate',
            'wrongSortToWrongLineABCount',
            'wrongSortToWrongHubACount',
            'wrongSortToWrongWhsBCount',
            'wrongSortToWrongLineCCount',
        ];
        if (wrongSortDrills.includes(drillType)) {
            cols.push(
                {
                    title: dt('missortedLogicType'),
                    dataIndex: 'missortedLogicType',
                    key: 'missortedLogicType',
                    render: (v: unknown) => {
                        const code = typeof v === 'number' ? v : Number(v);
                        if (code === 1) return dt('missortedLogicType1');
                        if (code === 2) return dt('missortedLogicType2');
                        return '-';
                    },
                },
                {
                    title: dt('wrongSortType'),
                    dataIndex: 'wrongSortType',
                    key: 'wrongSortType',
                    render: (v: unknown) => (v as string)?.trim() || '-',
                },
                {
                    title: dt('shouldDeliveryAddress'),
                    dataIndex: 'shouldDeliveryAddress',
                    key: 'shouldDeliveryAddress',
                    render: (v: unknown) => (v as string)?.trim() || '-',
                },
                {
                    title: dt('actualDeliveryAddress'),
                    dataIndex: 'actualDeliveryAddress',
                    key: 'actualDeliveryAddress',
                    render: (v: unknown) => (v as string)?.trim() || '-',
                },
                {
                    title: dt('lastSortAccountName'),
                    dataIndex: 'lastSortOperationAccountName',
                    key: 'lastSortOperationAccountName',
                    render: (v: unknown) => (v as string)?.trim() || '-',
                },
                {
                    title: dt('lastSortAccountRole'),
                    dataIndex: 'lastSortOperationAccountRole',
                    key: 'lastSortOperationAccountRole',
                    render: (v: unknown) => (v as string)?.trim() || '-',
                },
                {
                    title: dt('lastSortOperationTime'),
                    dataIndex: 'lastSortOperationTime',
                    key: 'lastSortOperationTime',
                    render: (v: unknown) => formatUtcToPacific(v as string | null),
                },
                {
                    title: dt('missortScanDriverName'),
                    dataIndex: 'wrongLineMissortScanDriverName',
                    key: 'wrongLineMissortScanDriverName',
                    render: (v: unknown) => (v as string)?.trim() || '-',
                },
                {
                    title: dt('missortScanDspName'),
                    dataIndex: 'wrongLineMissortScanDspName',
                    key: 'wrongLineMissortScanDspName',
                    render: (v: unknown) => (v as string)?.trim() || '-',
                },
                {
                    title: dt('isLastBaggingCorrect'),
                    dataIndex: 'isLastBaggingCorrect',
                    key: 'isLastBaggingCorrect',
                    // -1 是数仓判定的「没有装包记录」，与「装包不正确」不是一回事，要把话说清楚而不是留 '-'；
                    // 其余值（null = 数仓未给值/未回填）语义不明，仍然留 '-'，不冒充成「没有装包记录」
                    render: (v: unknown) => {
                        const flag = Number(v);
                        if (flag === 1) return dt('isLastBaggingCorrectYes');
                        if (flag === 0) return dt('isLastBaggingCorrectNo');
                        if (flag === -1) return dt('isLastBaggingCorrectNoRecord');
                        return '-';
                    },
                },
                {
                    title: dt('lastBaggingBagNumber'),
                    dataIndex: 'lastBaggingBagNumber',
                    key: 'lastBaggingBagNumber',
                    render: (v: unknown) => (v as string)?.trim() || '-',
                }
            );
        }
        if (businessType === WarehouseBusinessType.SORTING) {
            const sortingCols: typeof cols = [
                {
                    title: dt('lastMileServiceProviderName'),
                    dataIndex: 'lastMileServiceProviderName',
                    key: 'lastMileServiceProviderName',
                    render: (v: unknown) => (v as string)?.trim() || '-',
                },
            ];
            if (hubSortingCode !== 'LAXSTEMU') {
                sortingCols.push({
                    title: dt('sortErrorType'),
                    dataIndex: 'sortErrorType',
                    key: 'sortErrorType',
                    render: (v: unknown) => {
                        const n = Number(v);
                        if (!Number.isFinite(n) || n <= 0) return '-';
                        return t(`dashboard.${dashboardVariant}.detailDrawer.sortErrorType${n}`, { defaultValue: '-' });
                    },
                });
            }
            cols.push(
                ...sortingCols,
                {
                    title: dt('lastSortingHubScanAccountName'),
                    dataIndex: 'lastSortingHubScanAccountName',
                    key: 'lastSortingHubScanAccountName',
                    render: (v: unknown) => (v as string)?.trim() || '-',
                },
                {
                    title: dt('lastSortingHubScanTime'),
                    dataIndex: 'lastSortingHubScanTime',
                    key: 'lastSortingHubScanTime',
                    render: (v: unknown) => formatUtcToPacific(v as string | null),
                },
                {
                    title: dt('lastSortingHubScanTrackingEvent'),
                    dataIndex: 'lastSortingHubScanTrackingEvent',
                    key: 'lastSortingHubScanTrackingEvent',
                    render: (v: unknown) => {
                        const s = (v as string)?.trim();
                        if (!s) return '-';
                        return t(`enum.orderEvent.${stripOrderEventDataPrefix(s)}`, { defaultValue: s });
                    },
                },
                {
                    title: dt('lastSortingHubScanBagNumber'),
                    dataIndex: 'lastSortingHubScanBagNumber',
                    key: 'lastSortingHubScanBagNumber',
                    render: (v: unknown) => (v as string)?.trim() || '-',
                },
                {
                    title: dt('extSortingRule'),
                    dataIndex: 'extSortingRule',
                    key: 'extSortingRule',
                    render: (v: unknown) => (v as string)?.trim() || '-',
                }
            );
        }
        cols.push({
            title: dt('overallStatus'),
            dataIndex: 'overallStatus',
            key: 'overallStatus',
            render: (overallStatus: unknown) => {
                const s = (overallStatus as string | null | undefined) ?? null;
                if (!s) return '-';
                return t(`enum.overallStatus.${s}`, { defaultValue: s });
            },
        });
        return cols;
    }, [isAllUs, drillType, dt, i18n.resolvedLanguage, t, businessType, dashboardVariant, hubSortingCode]);

    const pagination = useMemo(
        () => ({
            current: page,
            pageSize,
            total: totalElements,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total: number) => t('table.pagination.totalItems', { total }),
            onChange: (p: number, size?: number) => {
                setPage(p);
                if (size != null) setPageSize(size);
            },
        }),
        [page, pageSize, totalElements, t, i18n.resolvedLanguage]
    );

    const copyTrackingNumbers = useCallback(async () => {
        if (!selectedRowKeys.length) {
            message.warning(t('central.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        const keySet = new Set(selectedRowKeys.map((k) => String(k)));
        const trackingNumbers = isCrossBorderOnline24h
            ? crossBorderContent
                  .filter((record) => keySet.has(String(record.trackingNumber ?? '')))
                  .map((record) => (record.trackingNumber ?? '').trim())
                  .filter((tn): tn is string => Boolean(tn))
            : content
                  .filter((record) => keySet.has(String(record.id ?? '')))
                  .map((record) => (record.trackingNumber ?? '').trim())
                  .filter((tn): tn is string => Boolean(tn));
        if (!trackingNumbers.length) {
            message.warning(t('central.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        const text = trackingNumbers.join('\n');
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            message.success(t('common.messages.copied'));
        } catch (err) {
            console.error('Copy tracking numbers failed:', err);
            message.error(t('common.messages.copyFailed'));
        }
    }, [selectedRowKeys, content, crossBorderContent, isCrossBorderOnline24h, t]);

    /** 导出：调接口生成报告，有选中则只导出选中行，否则导出全部符合条件的数据 */
    const handleDownloadDetails = useCallback(async () => {
        if (exportLoading) {
            message.warning(dt('exportGenerating'));
            return;
        }
        await runExportWithCooldown(async () => {
            try {
                const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                let fileName: string | undefined;
                if (isCrossBorderOnline24h) {
                    const response = await generateCrossBorderReportMutation({
                        variables: {
                            filter: {
                                statDateStart,
                                statDateEnd,
                                hubId: safeHubId,
                                hubIds: safeSelectedHubIds.length > 0 ? safeSelectedHubIds : null,
                                customerName: customerName ?? null,
                                // 本表 rowKey=trackingNumber（无 DB 行 ID），选中时直接透传运单号；后端映射到 tracking_number IN。
                                ids: selectedRowKeys.length > 0 ? selectedRowKeys.map((k) => String(k)) : null,
                            },
                            timezone: userTimezone,
                        },
                    });
                    fileName = response.data?.generateSortingCrossBorderOnline24hDetailDownloadReport;
                } else {
                    const baseFilter = {
                        statDateStart,
                        statDateEnd,
                        hubId: safeHubId,
                        hubIds: safeSelectedHubIds.length > 0 ? safeSelectedHubIds : null,
                        drillType: DRILL_TO_INPUT[drillType]!,
                        ids: selectedRowKeys.length > 0 ? selectedRowKeys.map((k) => String(k)) : null,
                        customerName: customerName ?? null,
                        businessType,
                    };
                    if (useHourlyDrill) {
                        const response = await generateHourlyReportMutation({
                            variables: {
                                filter: { ...baseFilter, windowStartHour, windowEndHour },
                                timezone: userTimezone,
                            },
                        });
                        fileName = response.data?.generateSortingHourlyDetailDownloadReport;
                    } else {
                        const response = await generateReportMutation({
                            variables: { filter: baseFilter, timezone: userTimezone },
                        });
                        fileName = response.data?.generateSortingDetailDownloadReport;
                    }
                }
                if (fileName) {
                    setGeneratedFileName(fileName);
                    setDownloadCompleteModalVisible(true);
                } else {
                    message.error(dt('exportFailed'));
                }
            } catch (error: unknown) {
                showError(error);
            }
        });
    }, [
        statDateStart,
        statDateEnd,
        hubId,
        selectedHubIds,
        drillType,
        selectedRowKeys,
        generateReportMutation,
        generateHourlyReportMutation,
        generateCrossBorderReportMutation,
        exportLoading,
        runExportWithCooldown,
        dt,
        safeHubId,
        safeSelectedHubIds,
        customerName,
        businessType,
        useHourlyDrill,
        windowStartHour,
        windowEndHour,
        isCrossBorderOnline24h,
    ]);

    const drawerTitleWithNote = (
        <>
            <span className="text-lg font-semibold border-l-4 border-purple-600 pl-2">{title}</span>
            <span className="text-sm font-normal ml-3">（当前明细页均为美西时间，包裹详情页仍为本地时间）</span>
        </>
    );

    return (
        <EditableDrawer
            title={drawerTitleWithNote}
            visible={visible}
            onClose={onClose}
            width={900}
            showDefaultFooter={false}
        >
            {isAllUs && (
                <Row gutter={[16, 16]} className="mb-4">
                    <Col>
                        <span className="mr-2">{dt('hubName')}</span>
                        <Select
                            mode="multiple"
                            allowClear
                            placeholder={dt('hubNamePlaceholder')}
                            style={{ minWidth: 220, maxWidth: 400 }}
                            value={selectedHubIds}
                            onChange={(v) => {
                                setSelectedHubIds(v ?? []);
                                setPage(1);
                                setSelectedRowKeys([]);
                            }}
                            options={hubOptions}
                            showSearch
                            filterOption={(inputVal, opt) =>
                                (opt?.label ?? '').toString().toLowerCase().includes(inputVal.toLowerCase())
                            }
                        />
                    </Col>
                </Row>
            )}
            <Row gutter={[16, 4]} className="mb-1" justify="end" align="middle">
                <Col>
                    <Space direction="vertical" size={4} className="items-end">
                        <Space>
                            {selectedRowKeys.length > 0 && (
                                <span className="text-gray-500 text-sm">
                                    {t('wms.delivery.deliveryCompletedSummary.selectedCount', {
                                        count: selectedRowKeys.length,
                                    })}
                                </span>
                            )}
                            <Button type="primary" onClick={copyTrackingNumbers}>
                                {t('common.buttons.copyTracking')}
                            </Button>
                            <Button
                                type="primary"
                                onClick={() => void handleDownloadDetails()}
                                loading={exportLoading}
                                disabled={exportLoading}
                            >
                                {getExportButtonLabel(
                                    selectedRowKeys.length > 0
                                        ? t('wms.delivery.deliveryCompletedSummary.exportSelected')
                                        : t('components.button.export')
                                )}
                            </Button>
                        </Space>
                        {businessType !== WarehouseBusinessType.SORTING && (
                            <span className="text-xs text-gray-500">导出的明细字段与parcels保持一致</span>
                        )}
                    </Space>
                </Col>
            </Row>
            {isCrossBorderOnline24h ? (
                <Table<SortingCrossBorderOnline24hDetailRow>
                    dataSource={crossBorderContent}
                    columns={crossBorderColumns}
                    rowKey="trackingNumber"
                    loading={tableLoading}
                    pagination={pagination}
                    scroll={{ x: 'max-content' }}
                    size="small"
                    rowSelection={{
                        selectedRowKeys,
                        onChange: (keys) => setSelectedRowKeys(keys),
                    }}
                />
            ) : (
                <Table
                    dataSource={content}
                    columns={baseColumns}
                    rowKey="id"
                    loading={tableLoading}
                    pagination={pagination}
                    scroll={{ x: 'max-content' }}
                    size="small"
                    rowSelection={{
                        selectedRowKeys,
                        onChange: (keys) => setSelectedRowKeys(keys),
                    }}
                />
            )}
            <CentralParcelDetailDrawer
                visible={parcelDetailVisible}
                orderId={selectedOrderId}
                userRole={getUserInfo()?.account?.role?.name ?? ''}
                onClose={() => setParcelDetailVisible(false)}
            />
            <AsyncReportDownloadModal
                visible={downloadCompleteModalVisible}
                onClose={() => setDownloadCompleteModalVisible(false)}
                fileName={generatedFileName}
                reportLabelI18nKey={dashboardAsyncReportLabelI18nKeys[dashboardVariant]}
            />
        </EditableDrawer>
    );
};

export default SortingDashboardDetailDrawer;
